import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';
import { ScrollReveal } from './ui/scroll-reveal';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="reviews" className="section" style={{ background: 'var(--bg-section)' }}>
      <div className="container">
        {/* Section Header */}
        <ScrollReveal animation="fadeUp">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span 
              className="inline-block text-sm font-semibold uppercase tracking-wider mb-4 px-4 py-1.5 rounded-full"
              style={{ 
                color: 'var(--accent-gold)',
                background: 'var(--accent-gold-light)'
              }}
            >
              Client Reviews
            </span>
            <h2 className="heading-1 mb-5">What Our Clients Say</h2>
            <p className="body-large">
              We take pride in delivering exceptional service. Here's what our 
              satisfied customers have to say.
            </p>
          </div>
        </ScrollReveal>

        {/* Main Testimonial */}
        <ScrollReveal animation="fadeUp" delay={200}>
          <div className="max-w-4xl mx-auto">
            <div 
              className="relative p-8 md:p-12 rounded-3xl"
              style={{ 
                background: 'var(--bg-card)',
                boxShadow: '0 20px 50px rgba(26, 62, 92, 0.08)',
                border: '1px solid var(--border-light)'
              }}
            >
              {/* Quote Icon */}
              <div 
                className="absolute top-8 right-8 w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: 'var(--accent-gold-light)' }}
              >
                <Quote size={28} style={{ color: 'var(--accent-gold)' }} />
              </div>

              {/* Stars */}
              <div className="flex gap-1.5 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={22} 
                    fill="var(--accent-gold)" 
                    color="var(--accent-gold)" 
                  />
                ))}
              </div>

              {/* Quote */}
              <blockquote 
                className="text-xl md:text-2xl leading-relaxed mb-8 pr-16"
                style={{ color: 'var(--text-primary)', fontWeight: 400 }}
              >
                "{testimonials[currentIndex].text}"
              </blockquote>

              {/* Author Info */}
              <div className="flex items-center justify-between flex-wrap gap-6">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                    style={{ background: 'var(--navy-primary)' }}
                  >
                    {testimonials[currentIndex].name.charAt(0)}
                  </div>
                  <div>
                    <p 
                      className="font-semibold text-lg"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {testimonials[currentIndex].name}
                    </p>
                    <p 
                      className="text-sm"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {testimonials[currentIndex].location} • {testimonials[currentIndex].service}
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-3">
                  <button 
                    onClick={prevTestimonial}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                    style={{ 
                      border: '2px solid var(--border-primary)',
                      background: 'transparent'
                    }}
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft size={22} style={{ color: 'var(--text-primary)' }} />
                  </button>
                  <button 
                    onClick={nextTestimonial}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                    style={{ 
                      background: 'var(--navy-primary)',
                      boxShadow: '0 4px 12px rgba(26, 62, 92, 0.3)'
                    }}
                    aria-label="Next testimonial"
                  >
                    <ChevronRight size={22} color="white" />
                  </button>
                </div>
              </div>

              {/* Progress Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setIsAutoPlaying(false);
                      setCurrentIndex(index);
                    }}
                    className={`h-2 rounded-full transition-all duration-500 ${
                      index === currentIndex ? 'w-8' : 'w-2'
                    }`}
                    style={{ 
                      background: index === currentIndex 
                        ? 'var(--navy-primary)' 
                        : 'var(--border-primary)'
                    }}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Mini Cards */}
        <ScrollReveal animation="fadeUp" delay={400}>
          <div className="grid md:grid-cols-3 gap-4 mt-10 max-w-4xl mx-auto">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <button 
                key={testimonial.id}
                className={`p-5 rounded-xl text-left transition-all duration-300 hover:scale-105 ${
                  index === currentIndex ? 'ring-2' : ''
                }`}
                style={{ 
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-light)',
                  ringColor: 'var(--accent-blue)',
                  boxShadow: index === currentIndex ? '0 8px 24px rgba(26, 62, 92, 0.1)' : 'none'
                }}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
              >
                <div className="flex gap-0.5 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} fill="var(--accent-gold)" color="var(--accent-gold)" />
                  ))}
                </div>
                <p 
                  className="font-semibold mb-1"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {testimonial.name}
                </p>
                <p 
                  className="text-sm"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {testimonial.location}
                </p>
              </button>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default TestimonialsSection;
